import { app } from '../../app';
import { PositionType } from '../../../lib/defs/types';
import { CubeAnimation } from '../../animations/CubeAnimation';
import { BlockId, ColorId, CubeBlockProps } from '../../defs/block';
import {
  BlockEntity,
  despawnBlockEntity,
  spawnBlockEntity,
} from '../../entities/BlockEntity';
import {
  despawnOverlayEntity,
  spawnOverlayEntity,
} from '../../entities/OverlayEntity';
import { GameScene } from '../../GameScene';
import {
  blockIsBinded,
  blockIsFrozen,
  blockIterateAll,
} from '../../util/blockTools';
import { mapGetPan } from '../../util/mapTools';
import { IEffect } from './IEffect';
import { waitFor } from '../../../lib/util/waitFor';
import { BreakEffect } from './BreakEffect';
import { MergeEffect } from './MergeEffect';
import {
  Match2PowerupType,
  match2PowerupsObj,
} from 'src/replicant/match2/match2.ruleset';
import StateObserver from 'src/StateObserver';

// types
//-----------------------------------------------------------------------------
type BlockReplacer = () => BlockId;

export type CubeReplaceComboEffectOptions = {
  subject: BlockEntity;
  pair: BlockEntity[];
  all: BlockEntity[];
  replacer: BlockReplacer;
};

/*
    bomb+cube combo replace all blocks of cube color with replacement blocks then tap those blocks effect
*/
export class CubeReplaceComboEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _options: CubeReplaceComboEffectOptions;
  private _spawedBlocks: BlockEntity[] = [];

  // init
  //-------------------------------------------------------------------------
  constructor(scene: GameScene, options: CubeReplaceComboEffectOptions) {
    this._scene = scene;
    this._options = options;
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const position = this._options.subject.c.position.mapPosition;
    this._spawedBlocks = [];
    // get cube color
    const color = this._getCubeColor();

    // get basic blocks of given cube color
    const targets = this._getTargets(color);

    // merge all blocks
    await new MergeEffect(this._scene, this._options.all, position).execute();

    // animate
    await this._animate(position, targets, color);

    // replace target blocks
    await this._collideTargets(targets);

    await StateObserver.invoke.addMatch2RaceScore({
      goal: 'lollipop',
      score: targets.length,
    });
  }

  // private: core
  //-------------------------------------------------------------------------
  private _getTargets(color: ColorId): BlockEntity[] {
    const targets: BlockEntity[] = [];

    // iterate blocks
    blockIterateAll(this._scene, (base) => {
      // not if frozen or binded
      if (
        !blockIsFrozen(this._scene, base) &&
        !blockIsBinded(this._scene, base)
      ) {
        const block = base?.c.block;

        // if basic block type and target color matches basic block color, choose block
        if (block?.props.type === 'basic' && color === block.props.color) {
          targets.push(base);
        }
      }
    });

    // Add current chain to targets.
    targets.push(...this._options.pair);

    return targets;
  }

  private _onBeamReachTarget(target: BlockEntity) {
    new BreakEffect(this._scene, {
      position: target.c.position.mapPosition,
      size: target.c.block.props,
      color: 0xffffff,
    }).execute();
    despawnBlockEntity(this._scene, target);

    app.sound.play('button-power', {
      dupes: 2,
    });

    const spawnedEntity = spawnBlockEntity(
      this._scene,
      { id: this._options.replacer() },
      target.c.position.mapPosition,
    );

    // Add spawned block to list
    this._spawedBlocks.push(spawnedEntity);

    // Record power block created
    if (spawnedEntity.c.block?.props?.type in match2PowerupsObj) {
      this._scene.recordPowerBlockCreated(
        spawnedEntity.c.block.props.type as Match2PowerupType,
      );
    }
  }

  private async _collideTargets(targets: BlockEntity[]) {
    for (const spawnedEntity of this._spawedBlocks) {
      // Collide one by one, in sequence, await only if they have a parent (not been dismissed yet)
      if (spawnedEntity.c.view2d.view.parent) {
        await waitFor(200);
      }
      spawnedEntity.c.block.collide('attack');
    }
  }

  // private: animation
  //-------------------------------------------------------------------------
  private async _animate(
    position: PositionType,
    targets: BlockEntity[],
    color: ColorId,
  ) {
    // play sound
    app.sound.play('puzzle-combo-cube-replace', {
      rate: 1.4,
      pan: mapGetPan(position),
    });

    // create animation
    const animation = new CubeAnimation({
      color,
      position,
      targets,
    });

    animation.onBeamReachTarget = (target) => this._onBeamReachTarget(target);

    // spawn animation as overlay entity
    const entity = spawnOverlayEntity(
      this._scene,
      position,
      { width: 1, height: 1 },
      () => animation,
      'foreground',
    );

    // run animation
    await animation.start();

    // despawn entity
    animation.completed.then(() => despawnOverlayEntity(this._scene, entity));
  }

  // private: support
  //-------------------------------------------------------------------------
  private _getCubeColor(): ColorId {
    const pair = this._options.pair;
    const props1 = pair[0].c.block.props;
    const props2 = pair[1].c.block.props;

    // get cube color
    return props1.type === 'cube'
      ? props1.color
      : (props2 as CubeBlockProps).color;
  }
}
